// Formatting + status helpers shared across the app.

export const money = (n, ccy = "AED") =>
  `${ccy} ${Number(n).toLocaleString("en-AE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const initials = (name = "") =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Relative-ish label from an ISO-ish string already prepared in mock data.
export const relTime = (label) => label;

// Status pill styling by semantic bucket.
const STYLES = {
  neutral: "bg-slate-100 text-slate-600 border-slate-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
  primary: "bg-indigo-50 text-indigo-700 border-indigo-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
};

// Map a raw status string to a semantic bucket.
const STATUS_MAP = {
  // leads / inquiry
  New: "info", Contacted: "primary", Qualified: "success", Disqualified: "neutral",
  Draft: "neutral", "Pending Information": "warning", "Ready for Rate": "info",
  "Rate Requested": "info", Quoted: "primary", Negotiation: "warning",
  Won: "success", Lost: "neutral", Expired: "danger",
  // quotation
  "Approval Pending": "warning", Approved: "success", Sent: "primary",
  Revised: "warning", Accepted: "success", Rejected: "danger",
  // job
  Booked: "primary", "Documents Pending": "warning", Planned: "info",
  "In Progress": "primary", Departed: "info", Arrived: "info",
  Clearance: "warning", Delivery: "info", Delivered: "success",
  "Invoice Pending": "warning", Invoiced: "primary", Closed: "neutral",
  // credit
  Released: "success", "Advance Required": "warning", "Finance Hold": "danger",
  "Exception Required": "violet",
  // generic
  Overdue: "danger", "Due Today": "warning", Paid: "success", Open: "info",
  "Customs Hold": "danger", "Payment Hold": "danger", Delayed: "danger",
  "On Track": "success", Complete: "success", Pending: "neutral",
  "Match": "success", "Variance": "warning", "Duplicate": "danger",
  // Phase 4.5 — Finance Gate / Advance / Finance Hold / Operations Handoff
  RELEASED: "success", ADVANCE_REQUIRED: "warning", ON_HOLD: "danger",
  MANAGEMENT_EXCEPTION_REQUIRED: "violet", REVIEW_REQUIRED: "warning", REJECTED: "danger",
  DRAFT: "neutral", REQUESTED: "info", PAYMENT_PROOF_RECEIVED: "warning",
  RECEIVED_UNVERIFIED: "warning", PARTIALLY_RECEIVED: "warning", VERIFIED: "success",
  SHORTFALL: "danger", WAIVED_BY_APPROVAL: "violet", RECONCILED: "success", CANCELLED: "neutral",
  PENDING_OPERATIONS_REVIEW: "info", ACCEPTED: "success", REJECTED_INCOMPLETE: "danger",
  RETURNED_FOR_CLARIFICATION: "warning", OPERATIONS_ACCEPTED: "success",
  READY_FOR_OPERATIONS_HANDOFF: "primary", "Legacy / Verification Required": "neutral",
  Current: "success", "Needs Refresh": "warning", Stale: "danger",
  // Phase 3 commercial workflow (real backend statuses — see STATE_MACHINE.md)
  NEW: "info", CONTACTED: "primary", QUALIFIED: "success", CONVERTED: "success", DISQUALIFIED: "neutral",
  READY_FOR_FEASIBILITY: "info", FEASIBILITY_REVIEW: "warning", READY_FOR_RATE: "info",
  RATE_REQUESTED: "info", QUOTED: "primary", WON: "success", LOST: "danger",
  // Phase 4.7 — commercial outcome closure
  ON_HOLD_CUSTOMER: "warning",
  PASS: "success", CONDITIONAL: "warning", CONDITIONAL_PENDING: "warning", FAIL: "danger",
  PREPARING: "neutral", PENDING_APPROVAL: "warning", SENT: "primary",
  DETECTED: "warning", PENDING_VERIFICATION: "warning", INVALIDATED_BY_CHANGE: "danger",
  REJECTED_AS_ACCEPTANCE: "danger", READY_FOR_FINANCE_GATE: "success", "Not yet reviewed": "neutral",
};

export const statusBucket = (s) => STATUS_MAP[s] || "neutral";
export const statusClass = (s) => STYLES[statusBucket(s)] || STYLES.neutral;
export const bucketClass = (b) => STYLES[b] || STYLES.neutral;

export const PRIORITY = {
  P1: { label: "Critical", dot: "bg-red-500", text: "text-red-600", ring: "ring-red-200" },
  P2: { label: "Urgent", dot: "bg-amber-500", text: "text-amber-600", ring: "ring-amber-200" },
  P3: { label: "Normal", dot: "bg-blue-500", text: "text-blue-600", ring: "ring-blue-200" },
  P4: { label: "Low", dot: "bg-slate-400", text: "text-slate-500", ring: "ring-slate-200" },
};

export const channelMeta = {
  email: { label: "Email", color: "text-blue-600", bar: "border-l-blue-500", chip: "bg-blue-50 text-blue-700" },
  whatsapp: { label: "WhatsApp", color: "text-emerald-600", bar: "border-l-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
  call: { label: "Call", color: "text-indigo-600", bar: "border-l-indigo-500", chip: "bg-indigo-50 text-indigo-700" },
};
